"use client"

// Read-only family picker for the Custom-selection Product Economics view.
// Container wires the popover ↔ dialog state-sharing pattern; families are
// defined centrally on the Product Families page (?page=shopify-bundle-rules).
//
// `useProductFamilies` returns the LIGHT list (id/name/family_key/
// product_count) — fast even on large catalogs because the count comes from
// a single aggregate RPC instead of paginating line_items, and the list is
// cached at module scope per (integration, period) so re-opening the picker
// is instant.
//
// On selection the container CLOSES IMMEDIATELY and hands the parent the
// light family + a hydration promise. Parent shows a single «adding
// family…» indicator near the items list while the promise resolves; the
// table re-fetch fires only after the hydrate completes (single visible
// loading state instead of two sequential spinners).

import { useCallback, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Layers } from "lucide-react"
import { useProductFamilies } from "@/hooks/use-product-families"
import type { ProductFamily, ProductFamilyLight } from "@/types/shopify"
import { SetPickerPanel } from "./set-picker-panel"

interface SetPickerContainerProps {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  minimumOrderTotal: number
  /** Family IDs already in the list (for duplicate prevention) */
  excludeFamilyIds: Set<string>
  /** When true, the trigger button is disabled (e.g. parent is mid-hydrate on a previous selection). */
  disabled?: boolean
  /**
   * Fires immediately on click. The container closes its popover/dialog
   * synchronously. The second argument is a Promise that resolves to the
   * fully-hydrated ProductFamily (or null on 404). Parent awaits the
   * promise to surface a single «adding family…» indicator and to push
   * the resolved family into its items list.
   */
  onSelect: (
    family: ProductFamilyLight,
    hydratePromise: Promise<ProductFamily | null>,
  ) => void
}

export function SetPickerContainer({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  minimumOrderTotal,
  excludeFamilyIds,
  disabled = false,
  onSelect,
}: SetPickerContainerProps) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  // Families load once either view is open; shared fetch between popover ↔ dialog.
  // product_count is period-scoped to match the table row that will appear
  // after selection. Module-level cache makes re-opens instant.
  const { families, isLoading, hydrateFamily } = useProductFamilies({
    integrationId,
    period: { monthStartUtc, monthEndUtc, minimumOrderTotal },
    enabled: popoverOpen || dialogOpen,
  })

  const isExpandingRef = useRef(false)

  const handleSelect = useCallback((family: ProductFamilyLight) => {
    // Close picker immediately — the hydrate fetch is parent's concern.
    setPopoverOpen(false)
    setDialogOpen(false)
    // Hand off the in-flight hydrate as a Promise. Parent awaits it to know
    // when to push the family into its items list and surface a single
    // loading indicator. We catch here ONLY to swallow unhandled rejection
    // noise — the actual error is propagated through the original Promise
    // for the parent's await.
    const hydratePromise = hydrateFamily(family.id)
    hydratePromise.catch(() => undefined)
    onSelect(family, hydratePromise)
  }, [hydrateFamily, onSelect])

  const handleRequestExpand = useCallback(() => {
    isExpandingRef.current = true
    setPopoverOpen(false)
    setDialogOpen(true)
    queueMicrotask(() => { isExpandingRef.current = false })
  }, [])

  const handlePopoverOpenChange = useCallback((next: boolean) => {
    if (next) {
      setPopoverOpen(true)
      return
    }
    setPopoverOpen(false)
    if (isExpandingRef.current) return
  }, [])

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled}>
            <Layers className="h-4 w-4 mr-1.5" />
            <span>Add Family</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <SetPickerPanel
            families={families}
            isLoading={isLoading}
            excludeFamilyIds={excludeFamilyIds}
            onSelect={handleSelect}
            onRequestExpand={handleRequestExpand}
          />
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl w-full h-[75vh] p-0 flex flex-col gap-0">
          <DialogHeader className="px-4 pt-4 pb-3 border-b shrink-0">
            <DialogTitle>Add a product family</DialogTitle>
            <DialogDescription>
              One-click adds the family. Define families on the Product Families page.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            <SetPickerPanel
              families={families}
              isLoading={isLoading}
              excludeFamilyIds={excludeFamilyIds}
              onSelect={handleSelect}
              dense
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
